/**
 * Client consent persistence (RQ78378 / RQ78379).
 *
 * Stored in localStorage (synchronous, cookie-like web storage subject to
 * ePrivacy; the consent-state storage itself is the standard strictly-necessary
 * carve-out). This is the authoritative, synchronous source of truth for the
 * SDK gate; a best-effort server audit row mirrors it.
 */

import { createNadDebug } from '../utils/debugLog'
import {
  type ConsentCategories,
  type ConsentState,
  type SourceAction,
} from './consentTypes'

const log = createNadDebug('dw:cookie-consent')

// Global Privacy Control is not yet in the standard lib DOM types.
declare global {
  interface Navigator {
    globalPrivacyControl?: boolean
  }
}

const CONSENT_KEY = 'dw_consent'
const DEVICE_KEY = 'dw_device_id'
const LINKED_ACCOUNT_KEY = 'dw_consent_linked_account'

/** Generate a UUID, with a non-crypto fallback for environments lacking it. */
export function generateUuid(): string {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID()
  }
  // Fallback (test/older browsers): RFC4122-ish v4 from Math.random.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0
    const v = ch === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function safeLocalStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null
    return window.localStorage
  } catch {
    // Access can throw (privacy mode / blocked storage).
    return null
  }
}

/** Read (or lazily create + persist) the stable first-party device id. */
export function getDeviceId(): string {
  const ls = safeLocalStorage()
  if (!ls) return generateUuid()
  const existing = ls.getItem(DEVICE_KEY)
  if (existing) return existing
  const created = generateUuid()
  try {
    ls.setItem(DEVICE_KEY, created)
  } catch {
    /* best-effort */
  }
  return created
}

function isValidCategories(cats: ConsentCategories | undefined): cats is ConsentCategories {
  if (!cats || typeof cats !== 'object') return false
  return (
    typeof cats.analytics === 'boolean' &&
    typeof cats.functional === 'boolean' &&
    typeof cats.marketing === 'boolean'
  )
}

/**
 * Validate a parsed JSON candidate as a ConsentState; null on any mismatch
 * (fail-safe). The input is the untyped result of ``JSON.parse`` — we runtime-
 * check every field rather than trusting the declared shape.
 */
export function parseConsentState(raw: Partial<ConsentState> | null): ConsentState | null {
  if (!raw || typeof raw !== 'object') return null
  const { v, cats, src, ts, did, eventId } = raw
  if (
    typeof v !== 'string' ||
    !isValidCategories(cats) ||
    typeof src !== 'string' ||
    typeof ts !== 'string' ||
    typeof did !== 'string' ||
    typeof eventId !== 'string'
  ) {
    return null
  }
  return {
    v,
    cats: {
      analytics: cats.analytics,
      functional: cats.functional,
      marketing: cats.marketing,
    },
    src,
    ts,
    did,
    eventId,
  }
}

/** Read stored consent; null when missing or corrupt (→ treated as no consent). */
export function readConsent(): ConsentState | null {
  const ls = safeLocalStorage()
  if (!ls) return null
  const raw = ls.getItem(CONSENT_KEY)
  if (!raw) return null
  try {
    const parsed = parseConsentState(JSON.parse(raw))
    if (!parsed) {
      log({ event: 'state_read_corrupt' })
    }
    return parsed
  } catch {
    log({ event: 'state_read_parse_error' })
    return null
  }
}

/** Persist a consent decision. Returns the written state. */
export function writeConsent(params: {
  policyVersion: string
  cats: ConsentCategories
  src: SourceAction
}): ConsentState {
  const state: ConsentState = {
    v: params.policyVersion,
    cats: params.cats,
    src: params.src,
    ts: new Date().toISOString(),
    did: getDeviceId(),
    eventId: generateUuid(),
  }
  const ls = safeLocalStorage()
  if (ls) {
    try {
      ls.setItem(CONSENT_KEY, JSON.stringify(state))
    } catch {
      /* best-effort; in-memory state still drives the gate this session */
    }
  }
  log({
    event: 'state_written',
    v: state.v,
    src: state.src,
    analytics: state.cats.analytics,
    functional: state.cats.functional,
    marketing: state.cats.marketing,
  })
  return state
}

/**
 * The account id we've already written an `account_link` audit row for. Lets the
 * auth bridge skip re-linking on every reload (it only links on an actual
 * device→account or account-switch transition). Written only post-acknowledgment.
 */
export function getLinkedAccount(): string | null {
  return safeLocalStorage()?.getItem(LINKED_ACCOUNT_KEY) ?? null
}

export function setLinkedAccount(accountId: string): void {
  try {
    safeLocalStorage()?.setItem(LINKED_ACCOUNT_KEY, accountId)
  } catch {
    /* best-effort */
  }
}

/**
 * Read the browser Global Privacy Control signal. When true, non-essential
 * categories are forced off regardless of stored consent (absolute rule).
 */
export function readGpcSignal(): boolean {
  if (typeof navigator === 'undefined') return false
  return navigator.globalPrivacyControl === true
}
